<template>
    <q-list>
        <q-item class="q-py-none q-px-xs">
            <q-item-section class="col-5">
                <q-item-label class="text-grey-7">{{$t('calculator.service_fee')}}</q-item-label>
            </q-item-section>

            <q-item-section class="text-right">
                <q-item-label>
                    <div dir="ltr">
                        <span>
                        <currency-format
                            :value="fees"
                            hide-zero-decimal
                            hide-white-spaces
                            style="display: inline-block;"
                            sign="+"
                        />
                    </span><span> {{originCurrency}} </span>
                    </div>
                </q-item-label>
            </q-item-section>
        </q-item> <!-- Fees -->

        <q-item class="q-py-none q-px-xs">
            <q-item-section class="col-6">
                <q-item-label class="text-grey-7">{{!isIntroduced && hasWelcomeReward ? $t('calculator.welcome_reward') : $t('calculator.rewards')}}
                    <span v-if="reward.total > 0">
                        (<currency-format
                            :value="reward.total"
                            hide-zero-decimal
                            hide-white-spaces
                            style="display: inline-block;"
                        /> {{reward.symbol}})
                    </span>
                </q-item-label>
            </q-item-section>

            <q-item-section class="text-right">
                <q-item-label>
                    <span v-if="isIntroduced && !isUserVerified && accountAge < 30">
                        <q-btn
                            dense
                            no-caps
                            flat
                            color="blue"
                            :label="$t('users.verify_to_redeem')"
                            @click="$router.push({name: 'account', params:{tab: 'verification'}})"
                            style="text-decoration: underline"
                        />
                    </span>
                    <span v-else>
                        <q-btn
                            round
                            dense
                            color="primary"
                            size="sm"
                            icon="fa fa-info"
                            v-if="reward.total > 0 && !discount"
                            @click="displayRewardDisclaimer = true"
                            class="q-mr-xs"
                        />
                        <span dir="ltr">
                            <currency-format
                                :value="discount"
                                hide-zero-decimal
                                hide-white-spaces
                                style="display: inline-block;"
                                sign="-"
                            /> {{originCurrency}}
                        </span>
                    </span>
                </q-item-label>
            </q-item-section>
        </q-item> <!-- Reward -->

        <q-separator spaced />

        <div v-if="(discountedRateActivated && ['destination', 'origin'].includes(discountType)) || !discountedRateActivated">
            <q-item class="q-py-none q-px-xs">
                <q-item-section class="col-5">
                    <q-item-label class="text-grey-7">{{$t('calculator.total_to_pay')}} &nbsp;
                        <q-btn outline round dense color="primary" icon="fa fa-info" size="xs" @click="$emit('showDiscount')" v-if="hasTier"/>
                    </q-item-label>
                </q-item-section>

                <q-item-section class="text-right">
                    <q-item-label dir="ltr">
                        <span class="text-weight-bold">
                            <currency-format
                                :value="totalToPay"
                                hide-zero-decimal
                                hide-white-spaces
                                style="display: inline-block; font-size: 1.2rem"
                                :class="discountType === 'destination' ? 'text-positive' : ''"
                            />
                        </span><span class="text-weight-bold" :class="discountType === 'destination' ? 'text-positive' : ''"> {{originCurrency}} </span>
                    </q-item-label>
                </q-item-section>
            </q-item>

            <q-item class="q-py-none q-px-xs">
                <q-item-section class="col-5">
                    <q-item-label class="text-grey-7">{{$t('calculator.recipient_gets')}}</q-item-label>
                </q-item-section>

                <q-item-section class="text-right">
                    <q-item-label dir="ltr">
                        <span class="text-weight-bold text-grey-9">
                            <currency-format
                                :value="recipientGets"
                                hide-zero-decimal
                                hide-white-spaces
                                style="display: inline-block; font-size: 1.2rem"
                                :class="discountType === 'origin' ? 'text-positive' : ''"
                            />
                        </span><span class="text-weight-bold" :class="discountType === 'origin' ? 'text-positive' : ''"> {{ destCurrency }} </span>
                    </q-item-label>
                </q-item-section>
            </q-item>
        </div>

        <q-separator spaced v-if="!discountedRateActivated && !discountType"/>

        <q-item class="q-py-none q-px-xs">
            <q-item-section class="col-5">
                <q-item-label class="text-grey-7">{{$t('calculator.transfer_time')}} &nbsp;<q-btn outline round dense color="primary" icon="fa fa-info" size="xs" @click="$emit('showDisclaimer')"/></q-item-label>
            </q-item-section>

            <q-item-section class="text-right">
                <q-item-label dir="ltr">
                    {{transferTime}}
                </q-item-label>
            </q-item-section>
        </q-item>

        <q-dialog v-model="displayRewardDisclaimer">
            <q-card>
                <q-card-section>
                    <div class="text-h6">{{$t('calculator.unusable_rewards')}}</div>
                </q-card-section>

                <q-card-section class="q-pt-none">
                    <i18n path="calculator.you_can_use_your_x_symbol_when_you_pay_in_symbol_only" tag="p">
                        <template #total>
                            <currency-format
                                :value="reward.total"
                                hide-zero-decimal
                                hide-white-spaces
                                style="display: inline-block;"
                                class="text-bold"
                            />
                        </template>
                        <template #symbol>
                            <b>{{ reward.symbol }}</b>
                        </template>
                    </i18n>

                    <i18n path="calculator.the_minimum_order_size_to_redeem_the_reward_is_currency_symbol" tag="p">
                        <template #x>
                            <currency-format
                                :value="rewardMinTransferAmount"
                                hide-zero-decimal
                                hide-white-spaces
                                style="display: inline-block;"
                            /> <b>{{reward.symbol}}</b>
                        </template>
                    </i18n>
                </q-card-section>

                <q-card-actions align="right" class="bg-white text-primary">
                    <q-btn flat :label="$t('ok')" v-close-popup />
                </q-card-actions>
            </q-card>
        </q-dialog>
    </q-list>
</template>
<script>

export default {
    name: "CalculationDetails",
    props: {
        fees: {
            type: [String, Number],
            default: 0
        },
        reward: {
            type: Object,
            default: () => {}
        },
        isIntroduced: {
            type: Boolean,
            default: false
        },
        isUserVerified: {
            type: Boolean,
            default: false
        },
        accountAge: {
            type: Number,
            default: 0
        },
        discount: {
            type: Number,
            default: 0
        },
        hasWelcomeReward: {
            type: Boolean,
            default: true
        },
        totalToPay: {
            type: Number,
            default: 0
        },
        originCurrency: {
            type: String,
            default: 'NZD'
        },
        destAmount: {
            type: [String, Number],
            default: 0
        },
        recipientGets: {
            type: Number,
            default: 0
        },
        destCurrency: {
            type: String,
            default: ''
        },
        mainRate: {
            type: Number,
            default: 0
        },
        discountedRate: {
            type: Number,
            default: 0
        },
        rewardMinTransferAmount: {
            type: Number,
            default: 0
        },
        transferTime: {
            type: String,
            default: '2 hours to 2 working days'
        },
        hasTier: {
            type: Boolean,
            default: false
        },
        discountedRateActivated: {
            type: Boolean,
            default: false
        },
        discountType: {
            type: String,
            default: null
        },
    },
    computed: {},
    data() {
        return {
            displayDisclaimer: false,
            displayDiscountRules: false,
            displayRewardDisclaimer: false,
        }
    },
    methods: {},
    created() {
    },
    mounted() {
    },
    watch: {
        discountType(value) {
            this.$emit('typeChanged', value)
        }
    },
    components: {},
}
</script>
<style>

</style>
